import { SelectInputOption } from '@/components/inputs/SelectInput';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import {
  CommerceTextFields,
  CommerceTextOption,
  CommerceTextOptionType,
  FinalizationReasonsOption,
  Option,
} from '../models';
import { customerServiceApi } from '@/pages/CustomerService/services/customer-service.service';

/** Opciones con el nombre sin espacios sobrantes, en orden alfabético. */
export const mapToOptions = (items: Option[]): SelectInputOption[] =>
  items
    .map(({ id, name }) => ({ value: id, label: (name ?? '').replace(/\s+/g, ' ').trim() }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

const caseOptionsApi = customerServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getIssuesOptions: builder.query<PaginatedResponse<CommerceTextOption>, void>({
      query: () => ({
        url: '/issues',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getStagesOptions: builder.query<PaginatedResponse<Option>, void>({
      query: () => ({
        url: '/stages',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getBusinessesOptions: builder.query<PaginatedResponse<Option>, void>({
      query: () => ({
        url: '/business',
        params: {
          offset: 0,
          limit: 2000,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getDriversOptions: builder.query<SelectInputOption[], void>({
      query: () => ({
        url: '/drivers',
        params: {
          offset: 0,
          limit: 2000,
        },
      }),
      transformResponse: (response: PaginatedResponse<any>) =>
        mapToOptions(
          response.items.map(({ id, firstName, lastName }) => ({
            id,
            name: `${firstName ?? ''} ${lastName ?? ''}`,
          }))
        ),
      providesTags: ['CaseOptions'],
    }),
    getDriverFailuresOptions: builder.query<PaginatedResponse<Option>, void>({
      query: () => ({
        url: '/driverfailures',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getPrioritiesOptions: builder.query<PaginatedResponse<Option>, void>({
      query: () => ({
        url: '/priorities',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getFinalizationReasonsOptions: builder.query<
      PaginatedResponse<FinalizationReasonsOption & CommerceTextFields>,
      void
    >({
      query: () => ({
        url: '/finalizationreasons',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getFinancialResponsibilitiesOptions: builder.query<
      PaginatedResponse<Option>,
      void
    >({
      query: () => ({
        url: '/financial-responsibilities',
        params: {
          offset: 0,
          limit: 50,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    getCaseOptionsByType: builder.query<
      PaginatedResponse<Option>,
      { optionType?: string; paginated: PaginatedRequest }
    >({
      query: ({ optionType, paginated }) => ({
        url: `/${optionType}`,
        params: {
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      providesTags: ['CaseOptions'],
    }),
    createOption: builder.mutation<
      Option | FinalizationReasonsOption,
      {
        newOption: Omit<Option, 'id'> | Omit<FinalizationReasonsOption, 'id'>;
        optionType: string;
      }
    >({
      query: ({ newOption, optionType }) => ({
        url: `/${optionType}`,
        method: 'POST',
        body: newOption,
      }),
      invalidatesTags: ['CaseOptions'],
    }),
    deleteOption: builder.mutation<
      Option,
      { optionId: string; optionType: string }
    >({
      query: ({ optionId, optionType }) => ({
        url: `/${optionType}/${optionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CaseOptions'],
    }),
    /**
     * Ojito y lápiz de "Lo que ve el comercio". Se actualiza la lista en el
     * momento para que el ojito responda sin esperar al server, y se vuelve
     * atrás si falla.
     */
    updateCommerceText: builder.mutation<
      CommerceTextOption,
      {
        optionType: CommerceTextOptionType;
        optionId: string;
        changes: CommerceTextFields;
      }
    >({
      query: ({ optionType, optionId, changes }) => ({
        url: `/${optionType}/${optionId}/commerce-text`,
        method: 'PATCH',
        body: changes,
      }),
      async onQueryStarted({ optionType, optionId, changes }, { dispatch, queryFulfilled }) {
        const applyChanges = (draft: PaginatedResponse<CommerceTextOption>) => {
          const option = draft.items.find((item) => item.id === optionId);
          if (option) Object.assign(option, changes);
        };
        const patch =
          optionType === 'issues'
            ? dispatch(
                caseOptionsApi.util.updateQueryData('getIssuesOptions', undefined, applyChanges)
              )
            : dispatch(
                caseOptionsApi.util.updateQueryData(
                  'getFinalizationReasonsOptions',
                  undefined,
                  applyChanges
                )
              );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['CaseOptions', 'Cases'],
    }),
    updateOption: builder.mutation<
      Option,
      {
        optionId: string;
        optionType: string;
        updatedOption: Omit<Option, 'id'>;
      }
    >({
      query: ({ optionId, optionType, updatedOption }) => ({
        url: `/${optionType}/${optionId}`,
        method: 'PUT',
        body: updatedOption,
      }),
      invalidatesTags: ['CaseOptions'],
    }),
  }),
});

export const {
  useGetIssuesOptionsQuery,
  useGetStagesOptionsQuery,
  useGetBusinessesOptionsQuery,
  useGetDriversOptionsQuery,
  useGetDriverFailuresOptionsQuery,
  useGetPrioritiesOptionsQuery,
  useGetFinalizationReasonsOptionsQuery,
  useGetFinancialResponsibilitiesOptionsQuery,
  useGetCaseOptionsByTypeQuery,
  useCreateOptionMutation,
  useDeleteOptionMutation,
  useUpdateOptionMutation,
  useUpdateCommerceTextMutation,
} = caseOptionsApi;
